import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
    CONSENT_OPEN_SETTINGS_EVENT,
    CONSENT_UPDATED_EVENT,
    applyConsent,
    getStoredConsent,
    saveConsent,
} from "../utils/cookieConsent";

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
    const [consent, setConsent] = useState(() => getStoredConsent());
    const [bannerVisible, setBannerVisible] = useState(() => !getStoredConsent());
    const [settingsOpen, setSettingsOpen] = useState(false);

    useEffect(() => {
        if (consent) {
            applyConsent(consent);
        }
    }, [consent]);

    useEffect(() => {
        const handleUpdated = (event) => {
            setConsent(event.detail);
            setBannerVisible(false);
        };
        const handleOpenSettings = () => {
            setSettingsOpen(true);
            setBannerVisible(false);
        };

        window.addEventListener(CONSENT_UPDATED_EVENT, handleUpdated);
        window.addEventListener(CONSENT_OPEN_SETTINGS_EVENT, handleOpenSettings);
        return () => {
            window.removeEventListener(CONSENT_UPDATED_EVENT, handleUpdated);
            window.removeEventListener(CONSENT_OPEN_SETTINGS_EVENT, handleOpenSettings);
        };
    }, []);

    const persistConsent = useCallback((nextConsent) => {
        const saved = saveConsent(nextConsent);
        setConsent(saved);
        setBannerVisible(false);
        setSettingsOpen(false);
    }, []);

    const openSettings = useCallback(() => {
        setSettingsOpen(true);
        setBannerVisible(false);
    }, []);

    const value = useMemo(
        () => ({
            consent,
            bannerVisible,
            settingsOpen,
            setSettingsOpen,
            persistConsent,
            openSettings,
        }),
        [consent, bannerVisible, settingsOpen, persistConsent, openSettings]
    );

    return (
        <CookieConsentContext.Provider value={value}>
            {children}
        </CookieConsentContext.Provider>
    );
}

export function useCookieConsent() {
    const context = useContext(CookieConsentContext);
    if (!context) {
        throw new Error("useCookieConsent must be used within CookieConsentProvider");
    }
    return context;
}

export function openCookieSettings() {
    window.dispatchEvent(new CustomEvent(CONSENT_OPEN_SETTINGS_EVENT));
}
